export const TERM_LANGUAGES = [
  { id: "en", label: "English", lang: "en", direction: "ltr" },
  { id: "fr", label: "Français", lang: "fr", direction: "ltr" },
  { id: "es", label: "Español", lang: "es", direction: "ltr" },
  { id: "zh", label: "中文 (简体)", lang: "zh-CN", direction: "ltr" },
  { id: "ko", label: "한국어", lang: "ko", direction: "ltr" },
  { id: "ar", label: "العربية", lang: "ar", direction: "rtl" },
];

export const TERMS_COPY = {
  en: {
    title: "Terms and Conditions for MyEnglish Lesson.ca",
    updated: "Last Updated: September 10, 2026",
    intro: "These Terms and Conditions (\"Terms\") govern your use of MyEnglish Lesson.ca and the instruction services provided by Tammam Mahdi in the Province of Ontario, Canada. By purchasing a lesson, subscribing to a plan, or completing a check-in, you agree to these Terms.",
    sections: [
      {
        id: "services-billing",
        testId: "terms-clause-acceptance",
        title: "1. Services and Billing",
        blocks: [{ type: "bullets", items: [
          "Prepayment Required: All single sessions, Class Pass memberships, and the 9-Class Private Pass Bundle must be fully prepaid prior to the start of any scheduled session.",
          "Standard Session Rates: Group English Practice is $32 CAD per session, and 1-on-1 Private Lessons are $55 CAD per session.",
          "9-Class Private Pass Bundle: Includes 9 full 1-on-1 private lessons per month at $390/month locked-in. At $43.33 per lesson, it saves over 21% off the standard $55 private rate. This private-only bundle does not include group sessions or Class Pass rebates.",
          "Currency: All fees, rates, and rebates listed on the platform are in Canadian Dollars (CAD$).",
          "Payment Processing: Primary payments (credit/debit cards) are processed securely through Stripe. By using Stripe, you agree to their respective terms of service.",
          "Alternative Payment Methods (Interac e-Transfer): Interac e-Transfer is accepted exclusively for selected multi-class plans and bundles upon manual agreement. e-Transfers are non-refundable once processed.",
        ] }],
      },
      {
        id: "class-pass",
        testId: "terms-clause-memberships-passes",
        title: "2. Class Pass Membership & Rebate Program",
        blocks: [{ type: "bullets", items: [
          "No Monthly Lesson Cap: Class Pass members have no limit on the total number of live group lessons they may attend within an active billing cycle.",
          "Class Pass Reward: The $20 CAD reward applies to standard $32 Group Practice and $55 Private Lesson sessions for up to 5 classes per month and 60 classes per year. It does not apply to the 9-Class Private Pass Bundle.",
          {
            text: "Rebate Criteria: To qualify for a $20 CAD rebate on an attended session, the student must:",
            numbered: [
              "Complete the official check-in process within the first 20 minutes of the scheduled lesson start time (measured in Eastern Standard Time / EST / EDT).",
              "Fully attend and participate in the entire duration of the session.",
            ],
          },
          "Exclusion of e-Transfer Payments: Rebates apply strictly to customers with active credit card subscriptions processed via Stripe. Purchases or plans funded via Interac e-Transfer are strictly ineligible for the Class Pass rebate program.",
          "Manual Settlement & Gating: All Class Pass rebate requests are logged in the CRM Refund Tracker and manually settled. Post-session recaps and class assets are held until the refund status is resolved (Issued Class Pass Refund or No Class Pass Refund).",
        ] }],
      },
      {
        id: "check-in-scheduling-capacity",
        testId: "terms-clause-attendance",
        title: "3. Check-In, Scheduling & Capacity",
        blocks: [{ type: "bullets", items: [
          "Time Zone: All lesson schedules, check-in windows, and automated emails operate strictly in Eastern Standard/Daylight Time (EST/EDT - Toronto Time).",
          "Class Size Limits: Group sessions are strictly capped at a maximum of 6 students to preserve real-time interaction quality.",
          "Cancellations & Absences: Prepaid single lessons or the 9 Private Lessons in a 9-Class Private Pass Bundle are forfeited if a student fails to attend or cancels within less than 24 hours' notice. Missed sessions do not qualify for Class Pass rebates.",
        ] }],
      },
      {
        id: "refunds-revisions",
        testId: "terms-clause-payments-refunds",
        title: "4. Refunds & Revisions",
        blocks: [{ type: "bullets", items: [
          "General Non-Refundability: Prepaid sessions, the 9-Class Private Pass Bundle, and active Class Pass subscription fees are non-refundable once charged, except where explicitly granted via the $20 Class Pass reward mechanism described in Section 2.",
          "Service Adjustments: MyEnglish Lesson.ca reserves the right to modify class schedules, instruction formats, or rebate tracking logic upon reasonable notice.",
        ] }],
      },
      {
        id: "governing-law",
        testId: "terms-clause-jurisdiction",
        title: "5. Governing Law",
        blocks: [{ type: "paragraph", text: "These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario and the applicable federal laws of Canada. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts located in Toronto, Ontario." }],
      },
      {
        id: "contact-information",
        testId: "terms-clause-contact",
        title: "6. Contact Information",
        blocks: [{ type: "paragraph", text: "For questions regarding billing, e-Transfers, or session schedules, please contact support directly through the MyEnglish Lesson.ca contact panel." }],
      },
    ],
  },
  fr: {
    title: "Conditions Générales pour MyEnglish Lesson.ca",
    updated: "Dernière mise à jour : 10 septembre 2026",
    intro: "Ces conditions générales (« Conditions ») régissent votre utilisation de MyEnglish Lesson.ca et les services d'enseignement fournis par Tammam Mahdi dans la province de l'Ontario, Canada. En achetant une leçon, en vous abonnant à un forfait ou en effectuant un enregistrement, vous acceptez ces Conditions.",
    sections: [
      {
        id: "services-billing",
        testId: "terms-clause-acceptance",
        title: "1. Services et facturation",
        blocks: [{ type: "bullets", items: [
          "Paiement préalable requis : Toutes les séances individuelles, les abonnements Class Pass et le 9-Class Private Pass Bundle doivent être entièrement payés avant le début de toute séance prévue.",
          "Tarifs standards : La pratique d'anglais en groupe coûte 32 $ CAD par séance et les leçons privées individuelles coûtent 55 $ CAD par séance.",
          "9-Class Private Pass Bundle : Ce forfait comprend 9 leçons privées individuelles complètes par mois au tarif bloqué de 390 $/mois. À 43,33 $ par leçon, il permet d'économiser plus de 21 % par rapport au tarif privé standard de 55 $. Il est exclusivement privé et n'inclut pas les séances de groupe ni les rabais Class Pass.",
          "Devise : Tous les frais, tarifs et rabais indiqués sur la plateforme sont en dollars canadiens (CAD$).",
          "Traitement des paiements : Les paiements principaux (cartes de crédit/débit) sont traités de manière sécurisée via Stripe.",
          "Méthodes de paiement alternatives (Virement Interac) : Le virement Interac est accepté exclusivement pour certains forfaits multi-cours sur accord manuel. Les virements Interac ne sont pas remboursables une fois traités.",
        ] }],
      },
      {
        id: "class-pass",
        testId: "terms-clause-memberships-passes",
        title: "2. Abonnement Class Pass et programme de rabais",
        blocks: [{ type: "bullets", items: [
          "Aucun plafond mensuel de cours : Les membres Class Pass n'ont aucune limite sur le nombre total de cours collectifs en direct auxquels ils peuvent assister au cours d'un cycle de facturation actif.",
          "Récompense Class Pass : La récompense de 20 $ CAD s'applique aux séances standards de pratique d'anglais en groupe à 32 $ et aux leçons privées à 55 $, jusqu'à 5 cours par mois et 60 cours par an. Elle ne s'applique pas au 9-Class Private Pass Bundle.",
          {
            text: "Critères de rabais : Pour bénéficier d'un rabais de 20 $ CAD, l'étudiant doit :",
            numbered: [
              "Compléter le processus d'enregistrement officiel dans les 20 premières minutes de l'heure de début prévue du cours (heure de l'Est / EST / EDT).",
              "Assister et participer pleinement à toute la durée de la séance.",
            ],
          },
          "Exclusion des paiements par virement Interac : Les rabais s'appliquent strictement aux clients disposant d'un abonnement par carte de crédit actif traité via Stripe. Les achats financés par virement Interac ne sont pas éligibles au programme de rabais Class Pass.",
          "Règlement manuel : Tous les rabais Class Pass sont enregistrés dans le Refund Tracker du CRM et réglés manuellement. Les récapitulatifs post-séance sont suspendus jusqu'à la résolution du statut (Issued Class Pass Refund ou No Class Pass Refund).",
        ] }],
      },
      {
        id: "check-in-scheduling-capacity",
        testId: "terms-clause-attendance",
        title: "3. Enregistrement, horaires et capacité",
        blocks: [{ type: "bullets", items: [
          "Fuseau horaire : Tous les horaires et fenêtres d'enregistrement fonctionnent strictement à l'heure de l'Est (EST/EDT - Heure de Toronto).",
          "Limites de taille de classe : Les séances de groupe sont strictement limitées à un maximum de 6 étudiants.",
          "Annulations et absences : Les séances individuelles prépayées ou les 9 leçons privées du 9-Class Private Pass Bundle sont confisquées en cas d'absence ou d'annulation à moins de 24 heures d'avis.",
        ] }],
      },
      {
        id: "refunds-revisions",
        testId: "terms-clause-payments-refunds",
        title: "4. Remboursements",
        blocks: [{ type: "paragraph", text: "Les séances prépayées, le 9-Class Private Pass Bundle et les abonnements Class Pass sont non remboursables, sauf disposition contraire explicite via le mécanisme de récompense Class Pass de 20 $." }],
      },
      {
        id: "governing-law",
        testId: "terms-clause-jurisdiction",
        title: "5. Droit applicable",
        blocks: [{ type: "paragraph", text: "Ces conditions sont régies par les lois de la province de l'Ontario et les lois fédérales du Canada." }],
      },
    ],
  },
  es: {
    title: "Términos y Condiciones para MyEnglish Lesson.ca",
    updated: "Última actualización: 10 de septiembre de 2026",
    intro: "Estos Términos y Condiciones (\"Términos\") rigen el uso de MyEnglish Lesson.ca y los servicios de instrucción prestados por Tammam Mahdi en la Provincia de Ontario, Canadá. Al comprar una clase, suscribirse a un plan o realizar el registro de entrada (check-in), usted acepta estos Términos.",
    sections: [
      {
        id: "services-billing",
        testId: "terms-clause-acceptance",
        title: "1. Servicios y facturación",
        blocks: [{ type: "bullets", items: [
          "Prepago obligatorio: Todas las clases individuales, membresías Class Pass y el 9-Class Private Pass Bundle deben pagarse por completo antes del inicio de cualquier sesión programada.",
          "Tarifas estándar: La práctica de inglés en grupo cuesta $32 CAD por sesión y las lecciones privadas individuales cuestan $55 CAD por sesión.",
          "9-Class Private Pass Bundle: Incluye 9 lecciones privadas individuales completas al mes por $390/mes con tarifa fija. A $43.33 por lección, ahorra más del 21% frente a la tarifa privada estándar de $55. Es un paquete exclusivamente privado y no incluye sesiones grupales ni reembolsos Class Pass.",
          "Moneda: Tarifas y reembolsos figuran en dólares canadienses (CAD$).",
          "Procesamiento de pagos: Los pagos principales se procesan de forma segura a través de Stripe.",
          "Transferencia Interac: Aceptada exclusivamente para planes seleccionados. No es reembolsable.",
        ] }],
      },
      {
        id: "class-pass",
        testId: "terms-clause-memberships-passes",
        title: "2. Membresía Class Pass y Programa de Reembolsos",
        blocks: [{ type: "bullets", items: [
          "Sin límite de clases: Los miembros de Class Pass no tienen límite en el número total de clases grupales en vivo a las que pueden asistir.",
          "Recompensa Class Pass: La recompensa de $20 CAD se aplica a las sesiones estándar de Práctica de Inglés en Grupo de $32 y a las Lecciones Privadas de $55, hasta 5 clases al mes y 60 clases al año. No se aplica al 9-Class Private Pass Bundle.",
          {
            text: "Criterios: El estudiante debe:",
            numbered: [
              "Completar el check-in dentro de los primeros 20 minutos de la hora de inicio programada (Hora Estándar del Este / EST / EDT).",
              "Asistir y participar plenamente en toda la clase.",
            ],
          },
          "Exclusión de Transferencia Interac: Los reembolsos se aplican estrictamente a clientes con tarjeta de crédito activa procesada a través de Stripe.",
          "Liquidación manual: Las actualizaciones y recursos posteriores a la sesión se retienen hasta que se resuelva el estado del reembolso (Issued Class Pass Refund o No Class Pass Refund).",
        ] }],
      },
      {
        id: "check-in-scheduling-capacity",
        testId: "terms-clause-attendance",
        title: "3. Registro, horarios y capacidad",
        blocks: [{ type: "bullets", items: [
          "Zona horaria: Opera estrictamente en Hora Estándar del Este (EST/EDT - Hora de Toronto).",
          "Límite de capacidad: Las sesiones grupales están estrictamente limitadas a un máximo de 6 estudiantes.",
          "Cancelaciones: Las clases individuales prepagadas o las 9 lecciones privadas del 9-Class Private Pass Bundle se pierden si no se asiste o si se cancela con menos de 24 horas de anticipación.",
        ] }],
      },
      {
        id: "refunds-revisions",
        testId: "terms-clause-payments-refunds",
        title: "4. Reembolsos",
        blocks: [{ type: "paragraph", text: "Las sesiones prepagadas, incluido el 9-Class Private Pass Bundle, y las membresías no son reembolsables, excepto mediante el mecanismo de recompensa Class Pass de $20." }],
      },
      {
        id: "governing-law",
        testId: "terms-clause-jurisdiction",
        title: "5. Ley aplicable",
        blocks: [{ type: "paragraph", text: "Regido por las leyes de la Provincia de Ontario y las leyes federales de Canadá." }],
      },
    ],
  },
  zh: {
    title: "MyEnglish Lesson.ca 服务条款",
    updated: "最后更新日期：2026年9月10日",
    intro: "本服务条款（“条款”）适用于您对 MyEnglish Lesson.ca 的使用以及 Tammam Mahdi 在加拿大安大略省提供的教学服务。购买课程、订阅计划或完成签到即表示您同意本条款。",
    sections: [
      {
        id: "services-billing",
        testId: "terms-clause-acceptance",
        title: "1. 服务与账单",
        blocks: [{ type: "bullets", items: [
          "必须预付款：所有单节课程、Class Pass 会员资格及 9-Class Private Pass Bundle 必须在任何预定课程开始前全额预付。",
          "标准课程价格：小组英语练习每节 32 加元，1 对 1 私人课程每节 55 加元。",
          "9-Class Private Pass Bundle：每月包含 9 节完整的一对一私人课程，固定价格为每月 390 加元。每节 43.33 加元，比标准每节 55 加元的私人课程节省超过 21%。该套餐仅包含私人课程，不包含小组课程或 Class Pass 返现。",
          "货币：平台列出的所有费用、费率和返现金额均以加拿大元 (CAD$) 计算。",
          "支付处理：主要付款（信用卡/借记卡）通过 Stripe 安全处理。",
          "Interac e-Transfer 电子转账：仅适用于经人工确认的特定多课时计划。一经处理概不退款。",
        ] }],
      },
      {
        id: "class-pass",
        testId: "terms-clause-memberships-passes",
        title: "2. Class Pass 会员与返现计划",
        blocks: [{ type: "bullets", items: [
          "无月度课程上限：Class Pass 会员在有效计费周期内参加的实时小组课程总数不受限制。",
          "Class Pass 奖励：20 加元奖励适用于每节 32 加元的小组英语练习和每节 55 加元的私人课程，每月最多 5 节、每年最多 60 节。不适用于 9-Class Private Pass Bundle。",
          {
            text: "返现标准：学生必须：",
            numbered: [
              "在预定开课时间的前 20 分钟内完成官方签到（以北美东部时间 EST/EDT 为准）。",
              "全程出席并积极参与整节课程。",
            ],
          },
          "排除电子转账支付：返现仅适用于通过 Stripe 处理的有效信用卡订阅客户。通过 Interac e-Transfer 支付的课程不享受 Class Pass 返现。",
          "人工结算：所有返现申请均在 CRM 中记录并由人工结算。课后复习资料将在退款状态确认（Issued Class Pass Refund 或 No Class Pass Refund）后发放。",
        ] }],
      },
      {
        id: "check-in-scheduling-capacity",
        testId: "terms-clause-attendance",
        title: "3. 签到、课程安排与容量",
        blocks: [{ type: "bullets", items: [
          "时区：所有课程安排和签到窗口严格按北美东部时间（EST/EDT - 多伦多时间）运行。",
          "人数限制：小组课程严格限制为最多 6 名学生。",
          "取消与缺席：若未能出席或提前不足 24 小时取消，预付的单节课程或 9-Class Private Pass Bundle 的 9 节私人课程将被没收。",
        ] }],
      },
      {
        id: "refunds-revisions",
        testId: "terms-clause-payments-refunds",
        title: "4. 退款",
        blocks: [{ type: "paragraph", text: "预付课程、9-Class Private Pass Bundle 和会员费概不退款，通过 20 加元 Class Pass 奖励机制明确授予的情况除外。" }],
      },
      {
        id: "governing-law",
        testId: "terms-clause-jurisdiction",
        title: "5. 适用法律",
        blocks: [{ type: "paragraph", text: "本条款受安大略省及加拿大适用联邦法律管辖。" }],
      },
    ],
  },
  ko: {
    title: "MyEnglish Lesson.ca 이용 약관",
    updated: "최종 수정일: 2026년 9월 10일",
    intro: "본 이용 약관(\"약관\")은 MyEnglish Lesson.ca의 사용 및 캐나다 온타리오주에서 Tammam Mahdi가 제공하는 수업 서비스에 적용됩니다. 수업 구매, 플랜 구독 또는 체크인을 완료하면 본 약관에 동의하는 것으로 간주됩니다.",
    sections: [
      {
        id: "services-billing",
        testId: "terms-clause-acceptance",
        title: "1. 서비스 및 결제",
        blocks: [{ type: "bullets", items: [
          "선불 필수: 모든 단일 수업, Class Pass 멤버십 및 9-Class Private Pass Bundle은 예정된 수업 시작 전에 전액 선불로 결제되어야 합니다.",
          "표준 수업 요금: 그룹 영어 연습은 세션당 $32 CAD이며, 1:1 개인 레슨은 세션당 $55 CAD입니다.",
          "9-Class Private Pass Bundle: 매월 완전한 1:1 개인 레슨 9회를 $390/월 고정 요금으로 제공합니다. 레슨당 $43.33로, 표준 $55 개인 레슨 요금보다 21% 이상 절약됩니다. 개인 레슨만 포함하며 그룹 수업이나 Class Pass 환급은 포함하지 않습니다.",
          "통화: 플랫폼에 표시된 모든 수수료, 요금 및 환급금은 캐나다 달러(CAD$) 기준입니다.",
          "결제 처리: 기본 결제는 Stripe를 통해 안전하게 처리됩니다.",
          "Interac e-Transfer 계좌이체: 사전 승인된 일부 수강권에 한해 수동으로 허용되며, 처리된 후에는 환불되지 않습니다.",
        ] }],
      },
      {
        id: "class-pass",
        testId: "terms-clause-memberships-passes",
        title: "2. Class Pass 멤버십 및 환급 프로그램",
        blocks: [{ type: "bullets", items: [
          "월 수업 제한 없음: Class Pass 회원은 유효한 결제 주기 동안 참석할 수 있는 라이브 그룹 수업의 총 횟수에 제한이 없습니다.",
          "Class Pass 환급: $20 CAD 환급은 세션당 $32 그룹 영어 연습과 세션당 $55 개인 레슨에 적용되며, 월 최대 5회 및 연 최대 60회까지 가능합니다. 9-Class Private Pass Bundle에는 적용되지 않습니다.",
          {
            text: "환급 조건:",
            numbered: [
              "정해진 수업 시작 시간 기준 첫 20분 이내에 공식 체크인을 완료해야 합니다 (동부 표준시 / EST / EDT 기준).",
              "전체 수업 시간 동안 완석하고 적극적으로 참여해야 합니다.",
            ],
          },
          "e-Transfer 결제 제외: 환급은 Stripe을 통해 처리된 활성 신용카드 구독 고객에게만 엄격하게 적용됩니다. e-Transfer로 결제된 건은 환급 대상에서 제외됩니다.",
          "수동 정산: 모든 환급 상태는 CRM에서 수동으로 정산되며, 상태가 확정(Issued Class Pass Refund 또는 No Class Pass Refund)될 때까지 수업 후 요약 자료 발송이 보류됩니다.",
        ] }],
      },
      {
        id: "check-in-scheduling-capacity",
        testId: "terms-clause-attendance",
        title: "3. 체크인, 일정 및 인원 제한",
        blocks: [{ type: "bullets", items: [
          "시간대: 모든 수업 일정 및 체크인 시간은 동부 표준시(EST/EDT - 토론토 시간)를 엄격히 따릅니다.",
          "인원 제한: 그룹 수업은 최대 6명으로 엄격히 제한됩니다.",
          "취소 및 불참: 24시간 전에 취소하지 않거나 불참할 경우 선불 단일 수업 또는 9-Class Private Pass Bundle의 개인 레슨 9회는 소멸됩니다.",
        ] }],
      },
      {
        id: "refunds-revisions",
        testId: "terms-clause-payments-refunds",
        title: "4. 환급 및 환불",
        blocks: [{ type: "paragraph", text: "선불 수업, 9-Class Private Pass Bundle 및 멤버십 비용은 환불되지 않으며, Class Pass $20 환급 프로그램을 통해 명시적으로 제공되는 경우는 예외입니다." }],
      },
      {
        id: "governing-law",
        testId: "terms-clause-jurisdiction",
        title: "5. 관할 법률",
        blocks: [{ type: "paragraph", text: "본 약관은 캐나다 온타리오주 및 캐나다 연방법의 적용을 받습니다." }],
      },
    ],
  },
  ar: {
    title: "الشروط والأحكام الخاصة بـ MyEnglish Lesson.ca",
    updated: "آخر تحديث: 10 سبتمبر 2026",
    intro: "تحكم هذه الشروط والأحكام (\"الشروط\") استخدامك لموقع MyEnglish Lesson.ca والخدمات التعليمية المقدمة من تمام مهدي في مقاطعة أونتاريو، كندا. عند شراء درس، أو الاشتراك في خطة، أو إكمال تسجيل الوصول، فإنك توافق على هذه الشروط.",
    sections: [
      {
        id: "services-billing",
        testId: "terms-clause-acceptance",
        title: "1. الخدمات والفواتير",
        blocks: [{ type: "bullets", items: [
          "الدفع المسبق مطلوب: يجب دفع جميع الجلسات الفردية، وعضويات Class Pass، و9-Class Private Pass Bundle بالكامل قبل بدء أي جلسة محددة.",
          "الأسعار القياسية: تبلغ ممارسة اللغة الإنجليزية الجماعية 32 دولارًا كنديًا للجلسة، وتبلغ الدروس الخاصة الفردية 55 دولارًا كنديًا للجلسة.",
          "9-Class Private Pass Bundle: يشمل 9 دروس خاصة فردية كاملة كل شهر بسعر ثابت قدره 390 دولارًا كنديًا شهريًا. بسعر 43.33 دولارًا للدرس، يوفر أكثر من 21٪ مقارنة بالسعر القياسي للدرس الخاص البالغ 55 دولارًا. هذه الباقة خاصة بالكامل ولا تشمل الجلسات الجماعية أو خصومات Class Pass.",
          "العملة: جميع الرسوم والخصومات الموضحة على المنصة هي بالـ دولار الكندي (CAD$).",
          "معالجة المدفوعات: تتم معالجة المدفوعات الأساسية بطريقة آمنة عبر Stripe.",
          "التحويل الإلكتروني (Interac e-Transfer): يُقبل حصريًا لخطة محددة بعد الموافقة اليدوية، وهو غير قابل للاسترداد بعد المعالجة.",
        ] }],
      },
      {
        id: "class-pass",
        testId: "terms-clause-memberships-passes",
        title: "2. برنامج عضوية Class Pass والخصومات",
        blocks: [{ type: "bullets", items: [
          "لا يوجد حد أقصى للدروس الشهرية: لا يوجد حد لأعداد الدروس الجماعية المباشرة التي يمكن لأعضاء Class Pass حضورها خلال دورة الفوترة النشطة.",
          "مكافأة Class Pass: تنطبق مكافأة قدرها 20 دولارًا كنديًا على جلسات ممارسة الإنجليزية الجماعية القياسية بقيمة 32 دولارًا والدروس الخاصة بقيمة 55 دولارًا، بحد أقصى 5 دروس شهريًا و60 درسًا سنويًا. ولا تنطبق على 9-Class Private Pass Bundle.",
          {
            text: "معايير الخصم: يجب على الطالب:",
            numbered: [
              "إكمال عملية تسجيل الوصول الرسمية خلال أول 20 دقيقة من وقت بدء الدرس المحدد (بتوقيت التوقيت الشرقي / EST / EDT).",
              "الحضور والمشاركة الكاملة طوال مدة الجلسة.",
            ],
          },
          "استثناء مدفوعات e-Transfer: تطبق الخصومات حصريًا على العملاء الذين لديهم اشتراكات بطاقات ائتمان نشطة تتم معالجتها عبر Stripe.",
          "التسوية اليدوية: يتم تسجيل جميع طلبات الخصم وتسويتها يدويًا في نظام CRM. ويتم تعليق إرسال ملخصات ما بعد الجلسة حتى يتم تحديد حالة الخصم (Issued Class Pass Refund أو No Class Pass Refund).",
        ] }],
      },
      {
        id: "check-in-scheduling-capacity",
        testId: "terms-clause-attendance",
        title: "3. تسجيل الوصول والجدولة والسعة",
        blocks: [{ type: "bullets", items: [
          "المكان والتوقيت: تعتمد جميع الجداول الزمنية حصريًا على التوقيت الشرقي (EST/EDT - توقيت تورونتو).",
          "حد السعة: تقتصر الجلسات الجماعية بشكل صارم على حد أقصى قدره 6 طلاب.",
          "الإلغاء والغياب: يتم إلغاء الجلسات الفردية المدفوعة مسبقًا أو الدروس الخاصة التسعة ضمن 9-Class Private Pass Bundle في حالة عدم الحضور أو الإلغاء قبل أقل من 24 ساعة.",
        ] }],
      },
      {
        id: "refunds-revisions",
        testId: "terms-clause-payments-refunds",
        title: "4. الاسترداد",
        blocks: [{ type: "paragraph", text: "الرسوم المدفوعة مسبقًا، بما في ذلك 9-Class Private Pass Bundle وعضويات Class Pass، غير قابلة للإرجاع، باستثناء ما يتم منحه صراحةً عبر آلية مكافأة Class Pass بقيمة 20 دولارًا كنديًا." }],
      },
      {
        id: "governing-law",
        testId: "terms-clause-jurisdiction",
        title: "5. القانون الحاكم",
        blocks: [{ type: "paragraph", text: "تخضع هذه الشروط لقوانين مقاطعة أونتاريو والقوانين الفيدرالية لـ كندا." }],
      },
    ],
  },
};

export const TERMS_DISCLAIMER = "Language & AI Disclaimer: Official legal agreements, platform communications, terms enforcement, and support disputes are conducted exclusively in English. Foreign language translations are provided for user convenience and accessibility (assisted by AI translation).";
